import type { DistributionRange } from "@/pages/home/lib/ranges";
import { formatMoneyShort, formatSignedMoneyShort, percentChange } from "../lib/format";
import { heroWhen } from "../lib/period";

const HERO_GRADIENT = "linear-gradient(90deg, #0e3c6b 0%, #332148 100%)";
const PORTRAIT_MASK = "radial-gradient(ellipse 55% 60% at 60% 45%, black 55%, transparent 100%)";
const LINE_DELAY_MS = 80;

type OverviewHeroProps = {
  range: DistributionRange;
  total: number | null;
  priorTotal: number | null;
  currency: string;
};

export function OverviewHero({ range, total, priorTotal, currency }: OverviewHeroProps) {
  const change = percentChange(total, priorTotal);
  const lines = total === null ? [] : [`${formatMoneyShort(total, currency)} GMV ${heroWhen(range)}`];

  return (
    <div
      style={{ backgroundImage: HERO_GRADIENT }}
      className="relative flex w-full shrink-0 flex-col items-start overflow-hidden pt-24 pb-[100px] pl-20"
    >
      <img
        aria-hidden="true"
        alt=""
        src="/home/hero-portrait.webp"
        style={{
          maskImage: PORTRAIT_MASK,
          WebkitMaskImage: PORTRAIT_MASK,
          animation: "portrait-fade-in 1.1s ease-out both, portrait-breathe 7s ease-in-out 1.1s infinite",
        }}
        className="pointer-events-none absolute top-[20px] right-[-30px] h-[560px] w-auto max-w-none object-cover"
      />

      <div key={`${range.from}-${range.to}`} className="relative z-10 flex flex-col gap-2">
        <p
          style={{ animation: "fade-in-up 0.6s ease-out both" }}
          className="text-2xl leading-[44px] font-normal tracking-[-0.48px] text-[#c0a7f2]"
        >
          All merchants at a glance
        </p>
        <h1 className="min-h-[88px] max-w-[760px] text-[36px] leading-[44px] font-medium tracking-[-0.72px] text-white">
          {lines.map((line) => (
            <span
              key={line}
              className="block"
              style={{ animation: `fade-in-up 0.7s cubic-bezier(0.22, 1, 0.36, 1) ${LINE_DELAY_MS}ms both` }}
            >
              {line}
            </span>
          ))}
          {change !== null && total !== null && priorTotal !== null && (
            <span
              className="block"
              style={{ animation: `fade-in-up 0.7s cubic-bezier(0.22, 1, 0.36, 1) ${LINE_DELAY_MS * 2}ms both` }}
            >
              <span className={change >= 0 ? "text-[#75e0a7]" : "text-[#fda29b]"}>
                {change >= 0 ? "up" : "down"} {Math.abs(change).toFixed(1)}%
              </span>{" "}
              ({formatSignedMoneyShort(total - priorTotal, currency)})
            </span>
          )}
        </h1>
      </div>
    </div>
  );
}
